import React, { useEffect, useState, useRef } from 'react';
import { errorMessages } from '../../utils/methods';
import AnimationPage from '../../pages/AnimationPage';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import MemberFilter from './MemberFilter/MemberFilter';
import MemberList from './memberList/MemberList';
import { Box, CircularProgress } from '@mui/material';
import CommonPagination from '../../form-components/CommonPagination';
import { getUsersList } from '../../api/member-management';
import QueryString from 'qs';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

const MemberIndex = () => {
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector(state => state.user.user);
  const prevPathnameRef = useRef(location.pathname);
  // URL 자체 갱신(handleChangePage 내 navigate)이 useEffect 재실행을 트리거할 때
  // 검색 조건이 초기화되지 않도록 자체 네비게이션을 표시하는 ref
  const isInternalNavigationRef = useRef(false);

  // URL 쿼리 파라미터에서 페이지 및 검색어 정보 읽기
  const searchParams = new URLSearchParams(location.search);
  const urlPage = searchParams.get('page');
  const urlSearchInput = searchParams.get('searchInput');
  const initialPage = urlPage ? parseInt(urlPage) : 1;

  // 필터 초기화용 순수 쿼리 (URL 파라미터 미포함)
  const initialQuery = {
    page: 1,
    limit: 25,
    headBranchId: user.headBranch?.headBranchId,
    branchId: user.branch?.branchId,
    searchInput: '',
    memberTypes: [],
    exerciseTypes: [],
    ticketTypes: [],
    classTypes: [],
    genders: [],
    ageRanges: [],
    smsReceiveList: [],
    memberGroupId: null,
    assignedTrainerId: null,
    // 방문경로(member.VISIT_PATH). null=전체
    visitPath: null,
    // 앱 연동(로그인 지점 기준). null=전체, 'Y' 연동, 'N' 미연동
    appLinked: null,
    // 보유 상품 필터 — products 는 UI 복원용, 서버는 아래 id 배열만 읽는다
    products: [],
    courseTicketIds: [],
    coursePassIds: [],
    trialTicketIds: [],
    lockerPassIds: [],
    remainingCounts: [],
    customRemainingCount: '',
    ticketExpireDateFrom: null,
    ticketExpireDateTo: null,
    lockerExpireDateFrom: null,
    lockerExpireDateTo: null,
    memberStopDateFrom: null,
    memberStopDateTo: null,
    // 등록일(목록 '등록일' 칸 기준), 구독권(Y=지금 구독 중), 수강권 공유(SHARING·SHARED, 지금 공유 중)
    registeredDateFrom: null,
    registeredDateTo: null,
    subscriptionActive: null,
    shareRoles: [],
    // 누적 방문수 필터 (visitMode/Year/Month/RecentDays는 UI 복원용, 백엔드는 visitDate·visitCount만 사용)
    visitMode: null,
    visitYear: null,
    visitMonth: null,
    visitRecentDays: null,
    visitCount: null,
    visitCountCondition: null,
    visitDateFrom: null,
    visitDateTo: null,
    sortBy: 'lastPaymentDate',
    sortDirection: 'DESC',
  };

  // 실제 초기 로드용 쿼리 (URL 파라미터 포함)
  const queryWithUrlParams = {
    ...initialQuery,
    page: initialPage,
    searchInput: urlSearchInput || '',
  };

  const [query, setQuery] = useState(queryWithUrlParams);
  // 마지막으로 조회를 실행한 조건 (MemberList의 선택 초기화 기준)
  const [appliedQuery, setAppliedQuery] = useState(queryWithUrlParams);
  const [members, setMembers] = useState({});
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false); // 테이블 로딩 (스켈레톤용)
  const [memberTypeCounts, setMemberTypeCounts] = useState(null); // 회원 구분별 카운트 (createBox 결과 기준)
  // createBox(상세검색) 칩의 '현재 선택' 집합 — 칩을 토글할 때마다 즉시 바뀐다(아직 검색 전).
  const [baseMemberTypes, setBaseMemberTypes] = useState(queryWithUrlParams.memberTypes || []);
  // '검색 실행'으로 실제 적용된 회원구분 집합 — memberList 탭 활성/비활성은 이걸 기준으로 한다.
  // (칩을 선택만 한 상태에선 탭 블라인드가 바뀌지 않고, '설정한 조건으로 회원검색'을 눌러야 반영)
  const [appliedMemberTypes, setAppliedMemberTypes] = useState(queryWithUrlParams.memberTypes || []);
  // memberList 상단에서 선택한 뷰 필터 탭(단일 type). null이면 base 전체를 본다.
  const [activeTab, setActiveTab] = useState(null);

  const { t } = useTranslation();

  // 회원목록 필터 상태(실제 쿼리 + base + 탭)를 localStorage에 저장 — 회원 상세 → 뒤로가기 복원용
  const persistMemberListState = (effectiveQuery, base, tab) => {
    localStorage.setItem('memberListQuery', JSON.stringify(effectiveQuery));
    localStorage.setItem('memberListBase', JSON.stringify(base));
    localStorage.setItem('memberListActiveTab', tab == null ? '' : tab);
  };

  // 검색 조건 초기화 시 URL 파라미터도 정리
  const handleFilterClear = () => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('searchInput')) {
      navigate('/member', { replace: true });
    }
  };

  const userList = async (token, query, updateCounts = false) => {
    setTableLoading(true);
    // 실제로 조회에 쓰인 조건. MemberFilter는 칩을 누를 때마다 query를 바꾸지만
    // 목록은 '검색' 실행 시에만 다시 부르므로, 선택 초기화 판단은 이 값을 기준으로 한다.
    setAppliedQuery(query);
    if (!user.headBranch?.headBranchId || (user.centerType === 'BRANCH' && !user.branch?.branchId)) {
      setMembers([]);
    } else {
      // products 는 UI 복원용 객체 배열 — 서버 파라미터가 아니라 전송에서 뺀다
      const queryString = QueryString.stringify({ ...query, products: undefined });
      try {
        await getUsersList(token, queryString).then(result => {
          setMembers(result);
          if (updateCounts && result.memberTypeCounts) {
            setMemberTypeCounts({
              total: result.totalElements || 0,
              active: result.memberTypeCounts.active || 0,
              scheduled: result.memberTypeCounts.scheduled || 0,
              expiringSoon: result.memberTypeCounts.expiringSoon || 0,
              stopped: result.memberTypeCounts.stopped || 0,
              unpaid: result.memberTypeCounts.unpaid || 0,
              lockerExpiring: result.memberTypeCounts.lockerExpiring || 0,
              expired: result.memberTypeCounts.expired || 0,
            });
          }
        });
      } catch (error) {
        console.error('API call failed:', error);
        toast.error(errorMessages(error));
      }
    }
    setLoading(false);
    setTableLoading(false);
  };

  const filterMember = qr => {
    // createBox 검색: 검색한 회원구분이 곧 base가 된다.
    // 단일 구분으로 검색했으면 그 탭을 활성화해 행 라벨을 그 구분으로 표시한다
    // (예: 중지회원으로 검색 → 유효 수강권이 있어도 전부 '중지회원'. 검색 결과는 모두 그 구분에 해당).
    // 다중/전체 검색이면 뷰 탭은 '전체'(null) → 행은 BE memberType(유효 우선)을 따른다.
    const base = qr.memberTypes || [];
    const nextActiveTab = base.length === 1 ? base[0] : null;
    const effectiveQuery = { ...qr, memberTypes: base };
    setBaseMemberTypes(base);
    setAppliedMemberTypes(base); // 검색 실행 시점에만 탭 블라인드 기준 갱신
    setActiveTab(nextActiveTab);
    // 카운트도 createBox 조건 기준으로 재계산 (백엔드가 memberTypes만 제외하고 집계)
    userList(user.token, effectiveQuery, true);
    setQuery(effectiveQuery);
    persistMemberListState(effectiveQuery, base, nextActiveTab);
  };

  // memberList의 정렬/limit 등 쿼리 변경을 setQuery + 저장으로 묶는다 (탭은 별도 핸들러)
  const commitQuery = updatedQuery => {
    setQuery(updatedQuery);
    persistMemberListState(updatedQuery, appliedMemberTypes, activeTab);
  };

  // memberList 상단 회원구분 탭 = createBox 결과를 타입별로 보는 "뷰 필터" (createBox 조건은 유지)
  const handleMemberTypeTabClick = type => {
    // "전체": 검색 적용된 base 집합 전체로 복귀
    if (type === null) {
      const effectiveQuery = { ...query, memberTypes: appliedMemberTypes, page: 1 };
      setActiveTab(null);
      setQuery(effectiveQuery);
      userList(user.token, effectiveQuery);
      persistMemberListState(effectiveQuery, appliedMemberTypes, null);
      return;
    }
    // 적용된 base가 지정돼 있는데 그 안에 없는 타입이면 비활성 탭 → 무시
    if (appliedMemberTypes.length > 0 && !appliedMemberTypes.includes(type)) return;
    const effectiveQuery = { ...query, memberTypes: [type], page: 1 };
    setActiveTab(type);
    setQuery(effectiveQuery);
    userList(user.token, effectiveQuery);
    persistMemberListState(effectiveQuery, appliedMemberTypes, type);
  };

  const handleChangePage = (event, value) => {
    const updatedQuery = { ...query, page: value };
    userList(user.token, updatedQuery);
    setQuery(updatedQuery);
    // 로컬스토리지에 query 전체 저장 (회원 상세 → 뒤로가기 시 필터 복원용)
    persistMemberListState(updatedQuery, appliedMemberTypes, activeTab);

    // URL의 page 파라미터도 동기화 → 새로고침 시에도 같은 페이지 유지
    isInternalNavigationRef.current = true;
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set('page', value);
    navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
  };

  useEffect(() => {
    // handleChangePage가 URL을 갱신해서 useEffect가 재실행된 경우, 데이터/필터 재초기화 스킵
    if (isInternalNavigationRef.current) {
      isInternalNavigationRef.current = false;
      prevPathnameRef.current = location.pathname;
      return;
    }

    // 이전 경로가 회원 상세 페이지인지 확인
    const isFromMemberDetail =
      localStorage.getItem('fromMemberDetail') === 'true' || prevPathnameRef.current?.match(/^\/member\/\d+$/); // /member/:id 패턴

    // Header에서 검색하여 navigate로 이동했는지 확인
    const isFromHeaderSearch = sessionStorage.getItem('headerSearchNavigation') === 'true';

    // 회원 상세에서 온 것이 아니면 localStorage 삭제
    if (!isFromMemberDetail) {
      // 다른 경로에서 온 경우 검색 조건 삭제
      localStorage.removeItem('memberListQuery');
      localStorage.removeItem('memberListBase');
      localStorage.removeItem('memberListActiveTab');
      // 상세 필터 펼침 상태(memberListFilterOpen)는 다른 메뉴에 다녀와도 유지한다(11/3) — 검색 조건만 초기화
    }

    // URL 쿼리 파라미터에서 페이지 및 검색어 정보 읽기
    const searchParams = new URLSearchParams(location.search);
    const urlPage = searchParams.get('page');
    let urlSearchInput = searchParams.get('searchInput');

    // Header에서 navigate로 온 것이 아니고 URL 파라미터가 있으면 (새로고침) 초기화
    if (urlSearchInput && !isFromHeaderSearch && !isFromMemberDetail) {
      navigate('/member', { replace: true });
      urlSearchInput = null;
    }

    // Header 검색 플래그 사용 후 삭제
    if (isFromHeaderSearch) {
      sessionStorage.removeItem('headerSearchNavigation');
    }

    // 저장된 query 읽기 (회원 상세에서 온 경우에만)
    const savedQueryStr = isFromMemberDetail ? localStorage.getItem('memberListQuery') : null;
    let savedQuery = null;
    if (savedQueryStr) {
      try {
        savedQuery = JSON.parse(savedQueryStr);
      } catch (error) {
        console.error('Failed to parse saved query:', error);
      }
    }

    // base(createBox 검색 집합) / activeTab(memberList 뷰 탭) 복원 — 회원 상세에서 온 경우에만
    let savedBase = null;
    let savedTab = null;
    if (isFromMemberDetail) {
      try {
        const baseStr = localStorage.getItem('memberListBase');
        savedBase = baseStr ? JSON.parse(baseStr) : null;
      } catch (error) {
        console.error('Failed to parse saved base:', error);
      }
      const tabStr = localStorage.getItem('memberListActiveTab');
      savedTab = tabStr ? tabStr : null;
    }

    const pageToUse = urlPage ? parseInt(urlPage) : savedQuery?.page || 1;

    // 저장된 query가 있으면 그것을 기반으로 업데이트, 없으면 initialQuery 사용
    const baseQuery = savedQuery || {
      ...initialQuery,
      headBranchId: user.headBranch?.headBranchId,
      branchId: user.branch?.branchId,
    };

    const updatedQuery = {
      ...baseQuery,
      page: pageToUse,
      branchId: user?.branch?.branchId || baseQuery.branchId,
      headBranchId: user?.headBranch?.headBranchId || baseQuery.headBranchId,
      // URL 검색어 파라미터가 있으면 우선 적용하고, memberTypes를 비워서 모든 회원 검색
      ...(urlSearchInput ? { searchInput: urlSearchInput, memberTypes: [] } : {}),
    };

    setQuery(updatedQuery);

    // base / 탭 상태 동기화
    if (isFromMemberDetail) {
      // 뒤로가기 복원: 저장된 base/탭 복원 (effective 쿼리는 탭이 적용된 값일 수 있음)
      const restoredBase = savedBase || updatedQuery.memberTypes || [];
      setBaseMemberTypes(restoredBase);
      setAppliedMemberTypes(restoredBase);
      setActiveTab(savedTab);
    } else {
      // 다른 경로 진입(헤더검색/사이드바/새로고침): 검색조건을 base로, 뷰 탭은 초기화
      const entryBase = updatedQuery.memberTypes || [];
      setBaseMemberTypes(entryBase);
      setAppliedMemberTypes(entryBase);
      setActiveTab(null);
    }

    // 초기 로드(+ 센터/지점/경로 변경) 시에만 탭 카운트도 함께 갱신. 이후 필터/페이지/정렬 변경은 카운트에 영향 없음
    userList(user.token, updatedQuery, true);

    // URL 검색어가 있으면 테이블로 스크롤 이동
    if (urlSearchInput) {
      setTimeout(() => {
        const tableElement = document.getElementById('member-list-table');
        if (tableElement) {
          tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }

    // 플래그 사용 후 삭제 및 경로 업데이트
    if (isFromMemberDetail) {
      // 복원한 상태(query + base + 탭)를 다시 저장 (다음에 다른 경로에서 오면 삭제됨)
      persistMemberListState(updatedQuery, savedBase || updatedQuery.memberTypes || [], savedTab);
      localStorage.removeItem('fromMemberDetail');
    }

    // 현재 경로를 이전 경로로 저장
    prevPathnameRef.current = location.pathname;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, user?.branch?.branchId, user?.headBranch?.headBranchId, user.token]);

  if (loading) {
    return (
      <Box sx={{ height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  return (
    <>
      <AnimationPage>
        <MemberFilter
          query={query}
          setQuery={setQuery}
          baseMemberTypes={baseMemberTypes}
          setBaseMemberTypes={setBaseMemberTypes}
          submitHandler={filterMember}
          initialQuery={initialQuery}
          onClear={handleFilterClear}
        />
      </AnimationPage>
      <MemberList
        query={query}
        setQuery={commitQuery}
        users={members}
        userList={userList}
        appliedQuery={appliedQuery}
        memberTypeCounts={memberTypeCounts}
        baseMemberTypes={appliedMemberTypes}
        activeTab={activeTab}
        onMemberTypeTabClick={handleMemberTypeTabClick}
        tableLoading={tableLoading}
      />
      <CommonPagination
        totalElements={members?.totalElements}
        limit={query.limit}
        page={query.page}
        onChange={handleChangePage}
      />
    </>
  );
};

export default MemberIndex;
